import FormSection from '@/components/Forms/FormSection';
import { intercomTarget } from '@/helpers/intercomTarget';
import usePermissions from '@/hooks/usePermissions';
import {
  Alert,
  Box,
  Button,
  FormikCheckboxInput,
  FormikRadioGroup,
  FormikSelectInput,
  FormikTextInput,
  toast,
} from '@palmetto/palmetto-components';
import { Field, FieldArray, useFormikContext } from 'formik';
import { Fragment } from 'react';

import {
  HVACAccessoryItem,
  HVACAccessoryLabels,
  HVACAccessoryType,
  HVACAirFilterType,
  HVACAirFilterTypeLabels,
  HVACZoningItem,
  isMiniSplitSystemCategory,
  UserPermissions,
  ZoningType,
} from 'types';

interface AccessoriesSectionProps {
  systemIndex: number;
  isPreliminary?: boolean;
  isPackageEditable?: boolean;
}

const dataKey = 'accessories' as const;
const label = 'Accessories' as const;

const accessoryTypes = Object.values(HVACAccessoryType).map((type) => ({
  label: HVACAccessoryLabels[type],
  value: type,
}));

const airFilterTypeOptions = Object.values(HVACAirFilterType).map((type) => ({
  id: type,
  label: HVACAirFilterTypeLabels[type],
  value: type,
}));

const miniSplitCoverageNoteMessage =
  'Minisplit systems do not come with accessories coverage as part of the Comfort Plan. All major equipment is fully covered.';

export const AccessoriesSection = ({
  systemIndex,
  isPreliminary,
  isPackageEditable = true,
}: AccessoriesSectionProps) => {
  const permissions = usePermissions();
  const userOnlyHasReaderPermission = permissions?.every((permission) =>
    permission.includes(UserPermissions.reader),
  );
  const { values, setFieldValue, handleBlur, errors, validateForm } = useFormikContext<any>();
  const isMiniSplit = isMiniSplitSystemCategory(values?.systems?.[systemIndex]?.systemCategory);

  return (
    <FormSection
      title={label}
      description={
        isPreliminary ? (
          'Toggle to enable/disable items'
        ) : (
          <Box gap="sm" fontSize="xs">
            <Box>Update applicable {label} for the proposal.</Box>
          </Box>
        )
      }
      baseStyle={{
        borderWidth: '0',
        'data-intercom-target': intercomTarget('system-design', label),
      }}
    >
      <FieldArray
        name={`systems.${systemIndex}[${dataKey}].items`}
        render={(arrayHelpers) => {
          return isPreliminary ? (
            <Box gap="lg">
              <Alert
                variant="warning"
                message={
                  isMiniSplit
                    ? miniSplitCoverageNoteMessage
                    : "Accessories not added to system design will not be covered by Palmetto's Extended Warranty. The consumables for accessories not included in system design is not covered under Palmetto's Preventative Maintenance Policy."
                }
              />
              <Box as="table" width="100%" style={{ borderCollapse: 'collapse' }}>
                <Box as="thead">
                  <Box as="tr" direction="row" gap="lg" fontSize="md">
                    <Box as="th" textAlign="left" padding="sm" minWidth="300px">
                      Accessory Type
                    </Box>
                    <Box as="th" textAlign="center" padding="sm" minWidth="50px">
                      Yes
                    </Box>
                    <Box as="th" textAlign="center" padding="sm" minWidth="50px">
                      No
                    </Box>
                    <Box as="th" textAlign="center" padding="sm" minWidth="50px">
                      Quantity
                    </Box>
                  </Box>
                </Box>
                <Box as="tbody">
                  {values.systems[systemIndex][dataKey].items.map((accessory: any, idx: number) => {
                    const itemPath = `systems.${systemIndex}[${dataKey}].items[${idx}]`;
                    return (
                      <Fragment key={accessory.type}>
                        <Box as="tr" direction="row" childGap="lg" alignItems="center">
                          <Box as="td" padding="sm" minWidth="300px">
                            {HVACAccessoryLabels[accessory.type as HVACAccessoryType]}
                          </Box>
                          <Box as="td" textAlign="center" padding="sm">
                            <Field
                              name={`${itemPath}.selected`}
                              component={FormikCheckboxInput}
                              checked={values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.selected}
                              isDisabled={
                                userOnlyHasReaderPermission ||
                                !values?.systems[systemIndex]?.systemCategory ||
                                !isPackageEditable ||
                                isMiniSplit
                              }
                              aria-label={`${accessory.label} is selected`}
                              onChange={async () => {
                                await setFieldValue(`${itemPath}.selected`, true);
                                await setFieldValue(`${itemPath}.notSelected`, false);
                                if (
                                  !values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.quantity ||
                                  values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.quantity === 0
                                ) {
                                  if (accessory.type === HVACAccessoryType.zoning) {
                                    await setFieldValue(`${itemPath}.quantity`, 2);
                                  } else {
                                    await setFieldValue(`${itemPath}.quantity`, 1);
                                  }
                                }
                                validateForm();
                              }}
                              onBlur={handleBlur}
                            />
                          </Box>
                          <Box as="td" textAlign="center" padding="sm">
                            <Field
                              name={`${itemPath}.notSelected`}
                              component={FormikCheckboxInput}
                              checked={values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.notSelected}
                              isDisabled={
                                userOnlyHasReaderPermission ||
                                !values?.systems[systemIndex]?.systemCategory ||
                                !isPackageEditable
                              }
                              aria-label={`${accessory.label} is not selected`}
                              onChange={async () => {
                                await setFieldValue(`${itemPath}.selected`, false);
                                await setFieldValue(`${itemPath}.notSelected`, true);
                                await setFieldValue(`${itemPath}.quantity`, 0);
                                await setFieldValue(`${itemPath}.airFilterType`, undefined);
                                validateForm();
                              }}
                              onBlur={handleBlur}
                            />
                          </Box>
                          <Box as="td" textAlign="center" padding="sm 0 sm 0">
                            <Field
                              name={`${itemPath}.quantity`}
                              type="number"
                              min={accessory.type === HVACAccessoryType.zoning ? 2 : 1}
                              max={10}
                              maxWidth="xl"
                              isDisabled={
                                userOnlyHasReaderPermission ||
                                !(
                                  typeof values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.selected ===
                                    'boolean' && values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.selected
                                ) ||
                                !values?.systems[systemIndex]?.systemCategory ||
                                !isPackageEditable ||
                                isMiniSplit
                              }
                              component={FormikTextInput}
                              aria-label={`Quantity for ${accessory.label}`}
                              validate={(value: any) => {
                                if (!values.systems?.[systemIndex]?.[dataKey]?.items?.[idx]?.selected) {
                                  return undefined;
                                }
                                const minValue = accessory.type === HVACAccessoryType.zoning ? 2 : 1;
                                const maxValue = 10;
                                const numValue = parseInt(value, 10);

                                if (value === '' || value === null || value === undefined) {
                                  return 'Quantity is required';
                                }
                                if (isNaN(numValue)) {
                                  return 'Quantity must be a valid number';
                                }
                                if (numValue < minValue) {
                                  return `Quantity must be at least ${minValue}`;
                                }
                                if (numValue > maxValue) {
                                  return `Quantity must not exceed ${maxValue}`;
                                }
                                return undefined;
                              }}
                            />
                          </Box>
                        </Box>
                        {accessory.type === HVACAccessoryType.airFilter && accessory.selected && (
                          <Box as="tr">
                            <Box as="td" colSpan={4} padding="0 sm md sm">
                              <Field
                                component={FormikRadioGroup}
                                id={`${itemPath}.airFilterType`}
                                name={`${itemPath}.airFilterType`}
                                title="Air filter type"
                                isRequired
                                options={airFilterTypeOptions}
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable || isMiniSplit}
                              />
                            </Box>
                          </Box>
                        )}
                      </Fragment>
                    );
                  })}
                  {Array.isArray(errors?.systems) && (errors.systems[systemIndex] as any)?.[dataKey]?.error && (
                    <Box as="tr">
                      <Box
                        as="td"
                        colSpan={4}
                        padding="sm"
                        color="danger"
                        fontSize="sm"
                        id={`systems.${systemIndex}.${dataKey}.error`}
                      >
                        {(errors.systems[systemIndex] as any)[dataKey].error}
                      </Box>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box gap="lg">
              {isMiniSplit && <Alert variant="warning" message={miniSplitCoverageNoteMessage} />}
              <Box direction={{ base: 'column', hd: 'row' }} gap="md">
                <Box maxWidth="3xl" width="100%">
                  <Field
                    label={`Add ${label}`}
                    name={`systems.${systemIndex}[${dataKey}].equipmentType`}
                    id={`systems.${systemIndex}[${dataKey}].equipmentType`}
                    placeholder={`Select new ${dataKey} type...`}
                    component={FormikSelectInput}
                    options={accessoryTypes}
                    isDisabled={
                      userOnlyHasReaderPermission ||
                      !values?.systems[systemIndex]?.systemCategory ||
                      !isPackageEditable ||
                      isMiniSplit
                    }
                    onChange={(event: { target: { value: { value: string } } }) => {
                      if (event.target.value.value === HVACAccessoryType.zoning) {
                        if (
                          values.systems[systemIndex][dataKey].items.some(
                            (item: HVACAccessoryItem) => item.type === HVACAccessoryType.zoning,
                          )
                        ) {
                          toast.error(
                            'Zoning accessory already exists for this system. Please update number of zones.',
                          );
                          setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, '');
                          return;
                        }
                      }
                      arrayHelpers.push({
                        type: event.target.value.value,
                        manufacturer: '',
                        model: '',
                        quantity: 1,
                        name: '',
                      });

                      setTimeout(() => {
                        const newIndex = values?.systems?.[systemIndex]?.[dataKey]?.items?.length ?? 0;
                        const element = document.getElementById(
                          `system-${systemIndex}-${dataKey}-${event.target.value.value}-${newIndex}`,
                        );
                        if (element) {
                          const rect = element.getBoundingClientRect();
                          const isInView =
                            rect.top >= 0 &&
                            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight);
                          if (!isInView) {
                            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                        }
                      }, 0);
                      setTimeout(() => {
                        setFieldValue(`systems.${systemIndex}[${dataKey}].equipmentType`, '');
                      }, 500);
                    }}
                  />
                </Box>
              </Box>
              {values?.systems[systemIndex]?.[dataKey]?.items
                ?.filter(
                  (item: HVACAccessoryItem | HVACZoningItem) =>
                    ![ZoningType.bypassDamper, ZoningType.zoneBoard, ZoningType.zoneDamper].includes(
                      item.type as ZoningType,
                    ),
                )
                .map?.((accessory: HVACAccessoryItem, index: number) => {
                  return (
                    <>
                      <Box
                        key={`system-${systemIndex}-${dataKey}-${accessory.type}-${index}`}
                        direction="row"
                        gap="lg"
                        justifyContent="space-between"
                        alignItems="center"
                        id={`system-${systemIndex}-${dataKey}-${accessory.type}-${index}`}
                      >
                        <Box direction="column" gap="sm" width={{ base: '60', tablet: '40' }} alignSelf="center">
                          <Box fontSize="md" fontWeight="medium">
                            {HVACAccessoryLabels[accessory.type]}
                          </Box>
                          {accessory.type !== HVACAccessoryType.zoning && (
                            <>
                              <Field
                                label={'Manufacturer'}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.manufacturer`}
                                component={FormikTextInput}
                                isRequired
                              />
                              <Field
                                label={accessory.type === HVACAccessoryType.airFilter ? 'Model/Size' : 'Model'}
                                name={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                id={`systems.${systemIndex}[${dataKey}].items.${index}.model`}
                                component={FormikTextInput}
                                isRequired
                              />
                              {accessory.type === HVACAccessoryType.airFilter && (
                                <Field
                                  component={FormikRadioGroup}
                                  id={`systems.${systemIndex}[${dataKey}].items.${index}.airFilterType`}
                                  name={`systems.${systemIndex}[${dataKey}].items.${index}.airFilterType`}
                                  title="Air filter type"
                                  isRequired
                                  options={airFilterTypeOptions}
                                  isDisabled={userOnlyHasReaderPermission || !isPackageEditable || isMiniSplit}
                                />
                              )}
                            </>
                          )}
                        </Box>
                        <Box direction="column" gap="sm" flex={{ tablet: 'auto' }}>
                          <Box direction={{ base: 'column', tablet: 'row' }} gap="sm" justifyContent="space-between">
                            <Field
                              label={accessory.type === HVACAccessoryType.zoning ? 'Zones' : 'Quantity'}
                              type="number"
                              name={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                              id={`systems.${systemIndex}[${dataKey}].items.${index}.quantity`}
                              component={FormikTextInput}
                              maxWidth="lg"
                              width="100"
                              min={accessory.type === HVACAccessoryType.zoning ? 2 : 1}
                              max={10}
                              isDisabled={userOnlyHasReaderPermission || !isPackageEditable || isMiniSplit}
                              validate={(value: any) => {
                                const minValue = accessory.type === HVACAccessoryType.zoning ? 2 : 1;
                                const maxValue = 10;
                                const numValue = parseInt(value, 10);

                                if (value === '' || value === null || value === undefined) {
                                  return 'Quantity is required';
                                }
                                if (isNaN(numValue)) {
                                  return 'Quantity must be a valid number';
                                }
                                if (numValue < minValue) {
                                  return `Quantity must be at least ${minValue}`;
                                }
                                if (numValue > maxValue) {
                                  return `Quantity must not exceed ${maxValue}`;
                                }
                                return undefined;
                              }}
                            />
                            <Box padding="lg 0 0 0">
                              <Button
                                as="button"
                                variant="tertiary"
                                tone="danger"
                                isDisabled={userOnlyHasReaderPermission || !isPackageEditable}
                                onClick={() => {
                                  if (arrayHelpers.form.values.systems[systemIndex][dataKey].items?.length === 1) {
                                    //If we remove the last item, we set up the totalCost to 0
                                    setFieldValue(`systems.${systemIndex}[${dataKey}].totalCost`, 0);
                                  }
                                  arrayHelpers.remove(index);
                                }}
                                style={{ padding: '0' }}
                              >
                                Delete
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      </Box>
                    </>
                  );
                })}
            </Box>
          );
        }}
      />
    </FormSection>
  );
};
